import type { Context } from "../context.js";
import type { EmitFn } from "../adapters.js";

/**
 * Back-guard — detecta que el usuario pulsa el botón ATRÁS del navegador para
 * mostrarle algo (un popup) ANTES de que se vaya. Es el equivalente, por la vía
 * del historial, del exit-intent (que mira el ratón / el flick táctil).
 *
 * TÉCNICA ("una vez y soltamos"):
 *  1. Tras un GESTO real del usuario (clic/tap/tecla) empujamos UNA entrada
 *     "centinela" al historial con la MISMA URL actual:
 *        history.pushState({...state, __twBackGuard:1}, "", location.href)
 *     Así el PRIMER Atrás cae sobre el centinela (NO navega: misma URL) y dispara
 *     un `popstate` en NUESTRA página en vez de sacar al usuario.
 *  2. En ese `popstate` ponemos la señal `backExitIntent=true` → el motor de reglas
 *     muestra el popup. NO volvemos a empujar el centinela → el SIGUIENTE Atrás
 *     navega de verdad (no atrapamos al usuario; mostramos una vez y soltamos).
 *
 * POR QUÉ EL GESTO ES OBLIGATORIO (no es un capricho):
 *   Chrome (y otros) marcan como "saltables" las entradas de historial creadas SIN
 *   activación del usuario, justo para impedir el secuestro del botón Atrás. Si
 *   empujáramos el centinela sin un gesto previo, el Atrás lo ignoraría y el truco
 *   no funcionaría. Por eso armamos solo tras un gesto que concede activación
 *   (pointerdown/touchstart/keydown). El scroll NO concede activación → no sirve.
 *   CONSECUENCIA HONESTA: un usuario que aterriza y pulsa Atrás sin haber tocado
 *   nada NO se captura. Es un límite del navegador, no del código.
 *
 * CÓMO DISTINGUIMOS "Atrás sobre esta página" de "Atrás que navega a otra" (dos defensas,
 * autosuficientes — NO dependen del router del publisher ni del nuestro, robustas con
 * spaEnabled apagado):
 *   (1) MARCADOR en la entrada. El centinela lleva `__twBackGuard` en su `history.state`. Si al
 *       hacer `popstate` ATERRIZAMOS sobre una entrada con ese marcador, es que el usuario está
 *       navegando hacia atrás por encima de un centinela ENTERRADO (el router del sitio empujó una
 *       ruta encima, o cambió solo query/hash con el mismo pathname) → NO disparamos. Esto es clave:
 *       el centinela enterrado comparte la URL de cuando armamos, así que la sola comparación de
 *       href NO bastaría para descartarlo.
 *   (2) URL. Si la URL cambió respecto a cuando armamos (`armedHref`), fue una navegación real, no
 *       el pop de nuestro centinela activo → NO disparamos.
 *   Solo disparamos cuando aterrizamos en una entrada REAL (sin marcador) con la MISMA URL: ahí el
 *   usuario popeó nuestro centinela activo para salir.
 *
 * RE-ARMADO (autosuficiente): `arm()` re-arma cuando la URL cambió desde que armamos (no depende de
 * onVirtualPageview, que solo resetea en cambios de pathname y con spaEnabled). Si la entrada actual
 * YA es un centinela nuestro (p. ej. re-armado tras bfcache), lo REUTILIZA en vez de apilar otro →
 * sin huérfanos adyacentes (que harían que el siguiente Atrás caiga en uno enterrado y no dispare).
 *
 * LÍMITES HONESTOS (inherentes a la técnica del centinela, no bugs):
 *   - Un `history.back()` PROGRAMÁTICO del sitio (botón "atrás" propio, cerrar modal) es
 *     indistinguible de un Atrás del usuario en `popstate` → puede disparar el popup. Acotado: una
 *     sola vez por página (maxFires/cooldown). La defensa por marcador cubre el caso común (modal
 *     abierto con pushState y cerrado con back).
 *   - Doble Atrás MUY rápido (dos pulsaciones en el mismo frame): el popup se pinta en el rAF del
 *     primero; si el segundo navega antes, la detección se emite pero el popup no llega a verse.
 *
 * "UNA VEZ Y SOLTAMOS" se refiere al CENTINELA: tras un Atrás no re-empujamos → el siguiente Atrás
 * navega de verdad. Cuántas VECES se muestra el popup lo gobierna `maxFiresPerSession` de la regla,
 * como en cualquier regla. Por defecto (maxFires=1) es 1 por página. Ojo: `backExitIntent` es un
 * LATCH (queda true hasta cambiar de pathname), igual que `exitIntent`; con maxFires>1 un scroll/idle
 * posterior puede re-mostrar el popup SIN un segundo Atrás → para "Atrás" se recomienda maxFires=1.
 *
 * NUNCA ROMPE: todo el acceso a `history` va en try/catch; si el publisher tiene un
 * history blindado (Object.freeze, accessor sin setter), degradamos a "no armar" sin
 * lanzar a la página.
 */
export type BackGuard = { resetForPage: () => void };

// Gestos que conceden activación de usuario (sticky activation). El scroll queda
// fuera a propósito: no concede activación, así que un centinela armado solo con
// scroll sería "saltable" por el navegador.
const GESTURES = ["pointerdown", "touchstart", "keydown"] as const;

/** ¿La entrada de historial (su `state`) es un centinela NUESTRO? `history.state` puede ser
 *  CUALQUIER valor estructurable (objeto, array, string, número, null), no solo objeto: el guard de
 *  typeof evita leer `.__twBackGuard` sobre un no-objeto y tratar por error un string del publisher
 *  como centinela. */
function isOurSentinel(state: unknown): boolean {
  return !!(state && typeof state === "object" && (state as Record<string, unknown>).__twBackGuard);
}

/**
 * @param ctx       estado vivo de señales.
 * @param emit      emisor de eventos.
 * @param shouldArm devuelve si AHORA mismo hay una regla de "Atrás" que se mostraría
 *                  (habilitada, sin agotar maxFires y fuera de cooldown). Solo tocamos
 *                  el historial cuando esto es cierto → cero impacto para quien no use
 *                  la feature, y nada de centinelas fantasma cuando el popup no saldría.
 */
export function startBackGuard(ctx: Context, emit: EmitFn, shouldArm: () => boolean): BackGuard {
  let armed = false; // centinela puesto y vigilando
  let fired = false; // ya disparamos en ESTA página (una vez y soltamos)
  let armedHref = ""; // URL en el momento de armar (para distinguir el pop del centinela)

  function arm() {
    if (fired) return; // ya disparamos en ESTA página → una vez y soltamos (hasta cambiar de página)
    if (armed && location.href === armedHref) return; // ya armado para ESTA url exacta → nada que hacer
    // Si llegamos aquí armados pero con OTRA url, navegamos desde que armamos (query/hash o ruta):
    // re-armamos para la url nueva. Esto hace al guard AUTOSUFICIENTE para re-armar, sin depender de
    // que onVirtualPageview nos resetee (que solo ocurre en cambios de pathname y con spaEnabled).
    if (!shouldArm()) return; // no hay popup de Atrás que mostrar → no ensuciamos el historial
    try {
      const st: unknown = history.state;
      armedHref = location.href;
      // Si la entrada ACTUAL ya es un centinela nuestro (re-armado tras bfcache/disarm en la misma
      // entrada), lo REUTILIZAMOS en vez de apilar otro: evita centinelas huérfanos ADYACENTES —que
      // harían que el siguiente Atrás aterrice en uno enterrado y NO dispare— y no infla el historial.
      if (isOurSentinel(st)) {
        armed = true;
        return;
      }
      // Solo "esparcimos" el state del publisher si es un OBJETO plano. Si es string/número/array
      // (raro pero válido en pushState), NO lo deformamos (un spread de "home" daría {0:'h',1:'o',…}):
      // empujamos un state limpio con solo nuestro marcador. La entrada del centinela es NUESTRA; las
      // entradas del publisher quedan intactas → honra "NUNCA ROMPE al publisher".
      const base =
        st && typeof st === "object" && !Array.isArray(st) ? (st as Record<string, unknown>) : null;
      history.pushState(base ? { ...base, __twBackGuard: 1 } : { __twBackGuard: 1 }, "", armedHref);
      armed = true;
    } catch {
      /* history no manipulable → degradamos a "sin back-guard" (nunca rompe) */
    }
  }

  function onGesture() {
    arm();
  }
  for (const g of GESTURES) {
    try {
      // capture+passive: no interferimos con el manejo del publisher; solo observamos.
      document.addEventListener(g, onGesture, { passive: true, capture: true });
    } catch {
      /* entorno sin addEventListener: degradación silenciosa */
    }
  }

  function onPopstate() {
    if (!armed) return; // no vigilábamos → no es asunto nuestro (lo gestiona el router)
    // (1) ¿Aterrizamos SOBRE una entrada centinela (lleva nuestro marcador)? Pasa cuando el
    // usuario navega hacia atrás por encima de un centinela ENTERRADO: armó en una página y luego
    // el router del sitio empujó otra ruta ENCIMA, o cambió solo query/hash con el MISMO pathname
    // (que no re-arma) → nuestro centinela quedó sepultado y el primer Atrás cae sobre él. Eso NO
    // es "salir de ESTA página", es una navegación interna normal → NO disparamos. Esta es la
    // defensa que hace al guard robusto incluso con spaEnabled=false (sin re-armado por ruta), donde
    // la sola comparación de href NO basta (el centinela enterrado comparte la URL de cuando armó).
    if (isOurSentinel(history.state)) {
      armed = false; // desarmamos (el usuario está navegando atrás, no saliendo de esta página)
      return;
    }
    // (2) ¿Cambió la URL respecto a cuando armamos? Entonces fue una navegación REAL (atrás a otra
    // página), no el pop de nuestro centinela activo. Desarmamos sin disparar.
    if (location.href !== armedHref) {
      armed = false;
      return;
    }
    // (3) Misma URL + aterrizamos en una entrada REAL (sin marcador) = el usuario popeó NUESTRO
    // centinela activo para salir → intención de volver atrás. Una vez y soltamos: no re-empujamos.
    armed = false;
    fired = true;
    if (ctx.state.backExitIntent) return;
    ctx.state.backExitIntent = true;
    // Agendamos la re-evaluación (el popup) ANTES de emitir: si un suscriptor de `back_intent`
    // (registrado por el publisher vía window.TW.on) lanzara, no impediría que el popup se muestre.
    ctx.touch(); // → re-evaluación de reglas → el popup se muestra
    emit("back_intent", {});
  }
  try {
    window.addEventListener("popstate", onPopstate, { passive: true });
  } catch {
    /* sin window utilizable: degradación silenciosa */
  }

  // bfcache: al restaurar la página desde el back-forward cache, TODO el estado JS vuelve intacto
  // (armed/fired y el centinela en el historial) pero SIN un gesto nuevo del usuario. Desarmamos
  // para no disparar sobre un centinela viejo sin interacción fresca; el próximo gesto re-arma.
  function onPageshow(e: PageTransitionEvent) {
    if (e.persisted) {
      armed = false;
      fired = false;
    }
  }
  try {
    window.addEventListener("pageshow", onPageshow as (e: Event) => void, { passive: true });
  } catch {
    /* degradación silenciosa */
  }

  return {
    // Lo llama index.ts cuando cambia el PATHNAME (página virtual nueva): resetea `fired` para
    // permitir un nuevo disparo en la página nueva. El re-armado por cambio de URL exacta (query/
    // hash) NO depende de aquí: lo hace arm() solo. En MPA cada carga reinicia el módulo.
    resetForPage() {
      armed = false;
      fired = false;
    },
  };
}
